import type { AiUsageRange, AiUsageSummary } from '@owl/shared';
import { DonutChart } from './DonutChart';

export function formatUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export function formatInt(n: number): string {
  return new Intl.NumberFormat('es-ES').format(n);
}

export function formatSeconds(s: number): string {
  if (s === 0) return '0s';
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return `${m}m ${r}s`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export function formatBucketLabel(iso: string, range: AiUsageRange): string {
  const d = new Date(iso);
  if (range === '30d' || range === '7d' || range === 'mtd') {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      day: '2-digit',
      month: '2-digit',
    }).format(d);
  }
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
}

export function Kpi({
  label,
  value,
  unit,
  delta,
}: {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string;
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">
        <span className="kpi-label-text">{label}</span>
      </div>
      <div className="kpi-value mono">
        {value}
        {unit && <span className="kpi-value-unit">{unit}</span>}
      </div>
      {delta && <div className="kpi-delta">{delta}</div>}
    </div>
  );
}

export function ChartsRow({
  summary,
  range,
}: {
  summary: AiUsageSummary;
  range: AiUsageRange;
}) {
  const t = summary.timeline;
  const totalCost = summary.totals.costUsd;
  const avg = t.length > 0 ? totalCost / t.length : 0;
  const avgLabel = range === '7d' || range === '30d' ? '/día' : '/h';

  const donutData = summary.byModel
    .filter((m) => m.costUsd > 0)
    .map((m) => ({
      value: m.costUsd,
      color: m.color,
      label: m.displayLabel,
    }));

  return (
    <div className="grid-overview-bottom">
      <div className="card">
        <div className="card-h">
          <div>
            <div className="card-t">Daily cost · {range}</div>
            <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4 }}>
              Coste por bucket en el rango seleccionado · línea oro = eventos
            </div>
          </div>
          <span className="label-xs mono">
            Ø {formatUsd(avg)}
            {avgLabel}
          </span>
        </div>
        <div className="card-body">
          <DailyCostChart timeline={t} range={range} />
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <div className="card-t">Breakdown por modelo</div>
        </div>
        <div
          className="card-body"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 18,
            alignItems: 'center',
          }}
        >
          {donutData.length === 0 ? (
            <div className="feed-empty" style={{ gridColumn: '1 / -1' }}>
              No cost in this range
            </div>
          ) : (
            <>
              <DonutChart
                data={donutData}
                centerLabel={`TOTAL · ${range.toUpperCase()}`}
                centerValue={formatUsd(totalCost)}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {donutData.map((d) => (
                  <div
                    key={d.label}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12 }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                    <span
                      className="mono"
                      style={{
                        letterSpacing: '0.04em',
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.label}
                    </span>
                    <span className="mono" style={{ marginLeft: 'auto', color: 'var(--fg-3)' }}>
                      {formatUsd(d.value)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function DailyCostChart({
  timeline,
  range,
}: {
  timeline: AiUsageSummary['timeline'];
  range: AiUsageRange;
}) {
  const maxCost = Math.max(0.0001, ...timeline.map((b) => b.costUsd));
  const maxEvents = Math.max(1, ...timeline.map((b) => b.events));
  const H = 80;
  const W = 100;
  const n = Math.max(1, timeline.length);
  const labelStep =
    range === '30d' ? Math.ceil(n / 8) : range === '7d' ? Math.ceil(n / 7) : Math.ceil(n / 6);

  if (timeline.length === 0) {
    return <div className="feed-empty">No data in this range</div>;
  }

  const points = timeline.map((b, i) => {
    const x = (i / Math.max(1, n - 1)) * W;
    const y = H - (b.costUsd / maxCost) * H * 0.9;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${(points[points.length - 1]?.x ?? 0).toFixed(2)} ${H} L ${(points[0]?.x ?? 0).toFixed(2)} ${H} Z`;

  const eventsPolyline =
    timeline.length > 1
      ? timeline
          .map((b, i) => {
            const x = (i / Math.max(1, n - 1)) * W;
            const y = H - (b.events / maxEvents) * H * 0.9;
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          })
          .join(' ')
      : null;

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 180, display: 'block' }}
      >
        <title>Daily cost and events</title>
        <defs>
          <linearGradient id="ai-cost-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#7AB8FF" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#7AB8FF" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line
            key={g}
            x1="0"
            y1={H - g * H * 0.9}
            x2={W}
            y2={H - g * H * 0.9}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="0.2"
          />
        ))}
        <path d={areaPath} fill="url(#ai-cost-fill)" />
        <path d={linePath} fill="none" stroke="#7AB8FF" strokeWidth="0.4" />
        {eventsPolyline && (
          <polyline fill="none" stroke="#D4B15C" strokeWidth="0.3" points={eventsPolyline} />
        )}
        {points.map((p, i) => (
          <circle key={timeline[i]?.bucket} cx={p.x} cy={p.y} r="0.5" fill="#7AB8FF" />
        ))}
      </svg>
      <div style={{ display: 'flex', marginTop: 6 }}>
        {timeline.map((b, i) => (
          <span
            key={`xc-${b.bucket}`}
            style={{
              flex: 1,
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--fg-4)',
              letterSpacing: '0.04em',
            }}
          >
            {i % labelStep === 0 ? formatBucketLabel(b.bucket, range) : ''}
          </span>
        ))}
      </div>
      <div className="chart-legend" style={{ marginTop: 8 }}>
        <span className="legend-item">
          <span className="legend-sq" style={{ background: '#7AB8FF' }} />
          <span style={{ fontSize: 10.5 }}>cost (USD)</span>
        </span>
        <span className="legend-item">
          <span className="legend-sq" style={{ background: '#D4B15C' }} />
          <span style={{ fontSize: 10.5 }}>events</span>
        </span>
      </div>
    </div>
  );
}
